import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EApiPath } from '../../enums';
import {
    IBulkDeleteResult,
    ICheckDeleteResult,
    QueryPayload,
} from '../models/api.models';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Thin wrapper around HttpClient for calling api_maker endpoints. Every service in
// this app goes through here instead of building URLs / query params by hand, so the
// backend host and tenant path are read from environment exactly once (per the
// project rule against hardcoding config). URL shapes match the proven reference
// implementation (sava_printing_b2c's ApiService/BaseApiService) exactly:
//   Custom API : {host}/api/custom-api/{ADMIN_PATH}/{CUSTOM_API_PATH}/{bareEndpointName}
//   Instance   : {host}/api/gen/{ADMIN_PATH}/{INS}/{DB}/{collection}
@Injectable({ providedIn: 'root' })
export class ApiService {
    private readonly http = inject(HttpClient);

    private buildParams(queryParams?: Record<string, string | number | boolean>): HttpParams {
        let params = new HttpParams();
        if (queryParams) {
            for (const [key, value] of Object.entries(queryParams)) {
                if (value !== undefined && value !== null) {
                    params = params.set(key, String(value));
                }
            }
        }
        return params;
    }

    private request<T>(method: HttpMethod, url: string, body: unknown, params: HttpParams): Observable<T> {
        switch (method) {
            case 'GET':
                return this.http.get<T>(url, { params });
            case 'POST':
                return this.http.post<T>(url, body, { params });
            case 'PUT':
                return this.http.put<T>(url, body, { params });
            case 'PATCH':
                return this.http.patch<T>(url, body, { params });
            case 'DELETE':
                return this.http.delete<T>(url, { params });
        }
    }

    // `endpointName` is the BARE endpoint the backend Custom API's `path` config ends
    // in (e.g. 'login', 'refresh-token', 'bootstrap-admin', 'lookups') — never a
    // leading-slash path, never the tenant segment. The tenant segment is injected
    // here from environment, matching how the backend's own `path` config
    // (e.g. '/shekhawat/login') registers the route server-side.
    callCustomApi<T = unknown>(
        endpointName: string,
        method: HttpMethod = 'POST',
        body?: unknown,
        queryParams?: Record<string, string | number | boolean>,
    ): Observable<T> {
        const url = `${environment.SHEKHAWAT_BE_HOST_PORT_SITES}/custom-api/${environment.ADMIN_PATH}/${environment.CUSTOM_API_PATH}/${endpointName}`;
        return this.request<T>(method, url, body, this.buildParams(queryParams));
    }

    // Schema CRUD (api_maker's /api/schema/{tenant}/{ins}/{db}/{collection} endpoints) —
    // used for simple reference collections (categories, products, customers) that don't
    // need a bespoke Custom API. Matches the reference project's (sava_printing_b2c_admin)
    // BaseApiService pattern exactly: /api/gen/... returns 403 "not authenticated" for
    // these staff-token-authenticated requests, while /api/schema/... is the route the
    // platform actually authenticates DB-auth end-user tokens against.
    private schemaUrl(collection: string, subRoute = ''): string {
        const base = `${environment.SHEKHAWAT_BE_HOST_PORT_SITES}/schema/${environment.ADMIN_PATH}/${environment.INS}/${environment.DB}/${collection}`;
        return subRoute ? `${base}/${subRoute}` : base;
    }

    // Server-side paginated read. The platform's /query route accepts find/sort/skip/limit
    // and returns the rows; the caller supplies limit/skip so lists never load a whole
    // collection into the browser.
    querySchemaApi<T = unknown>(collection: string, payload: QueryPayload): Observable<T[]> {
        return this.request<T[]>('POST', this.schemaUrl(collection, 'query'), payload, new HttpParams());
    }

    /** Row count matching a filter — pairs with querySchemaApi for totalCount. */
    countSchemaApi(collection: string, find: Record<string, unknown> = {}): Observable<number> {
        return this.request<number>('POST', this.schemaUrl(collection, 'count'), { find }, new HttpParams());
    }

    saveSchemaApi<T = unknown>(collection: string, payload: unknown): Observable<T> {
        return this.request<T>('POST', this.schemaUrl(collection, 'save-single-or-multiple'), payload, new HttpParams());
    }

    updateSchemaApiById<T = unknown>(collection: string, id: string, payload: unknown): Observable<T> {
        return this.request<T>('PUT', this.schemaUrl(collection, `update-by-id/${encodeURIComponent(id)}`), payload, new HttpParams());
    }

    deleteSchemaApiById<T = unknown>(collection: string, id: string): Observable<T> {
        return this.request<T>('DELETE', this.schemaUrl(collection, encodeURIComponent(id)), undefined, new HttpParams());
    }

    // -- Referential integrity ------------------------------------------------

    /**
     * Asks the backend whether a record can be safely deleted. Returns
     * `{ canDelete: true }` when nothing references it, otherwise
     * `{ canDelete: false, reason, usedIn }` listing every blocking record.
     * Call this before any destructive delete so the user gets a real
     * explanation instead of a foreign-key error from the database.
     */
    checkDelete(collection: string, id: string): Observable<ICheckDeleteResult> {
        return this.callCustomApi<ICheckDeleteResult>(EApiPath.CHECK_DELETE, 'POST', { collection, id });
    }

    /**
     * Deletes many records one at a time, checking referential integrity before
     * each. Records that fail the check — or whose delete call itself throws —
     * are skipped with a reason; everything else is deleted. A single failing
     * record never aborts the batch.
     *
     * Sequential by design: the check and the delete must stay paired per record,
     * and firing a whole batch in parallel would let a record be deleted after
     * something else in the same batch started depending on it.
     */
    bulkDeleteRecords<T extends { _id?: string }>(
        collection: string,
        records: T[],
        removeFn: (record: T) => Observable<unknown>,
    ): Observable<IBulkDeleteResult<T>> {
        const run = async (): Promise<IBulkDeleteResult<T>> => {
            const deleted: T[] = [];
            const skipped: IBulkDeleteResult<T>['skipped'] = [];

            for (const record of records) {
                const id = record._id;
                if (!id) {
                    skipped.push({ record, reason: 'Missing id' });
                    continue;
                }

                const check = await firstValueFrom(
                    this.checkDelete(collection, id).pipe(
                        catchError(() => of(null)),
                    ),
                );
                if (!check) {
                    skipped.push({ record, reason: 'Could not verify references.' });
                    continue;
                }
                if (!check.canDelete) {
                    skipped.push({
                        record,
                        reason: check.reason ?? 'This record is used elsewhere.',
                        usedIn: check.usedIn,
                    });
                    continue;
                }

                const removed = await firstValueFrom(
                    removeFn(record).pipe(
                        map(() => true),
                        catchError(() => of(false)),
                    ),
                );
                if (removed) deleted.push(record);
                else skipped.push({ record, reason: 'Delete failed.' });
            }

            return { deleted, skipped };
        };

        return from(run());
    }
}
